import * as React from 'react';
import { cx } from 'linaria';
import { listBoxStyle, listOptionStyle, addScrollStyle, openStyle, activeStyle } from './listbox.styles';

export interface ListBoxProps {
    id?: string;
    open?: boolean;
    value?: string;
    maxSize?: number;
    optionHeight?: number;
    'aria-labelledby'?: string;
}

const ListBoxContext = React.createContext({ value: '' });

export interface ListBoxItemProps {
    handleClick?: (optionValue: string) => void;
    id?: string;
    optionValue?: string;
}

const ListBoxItem: React.FunctionComponent<React.PropsWithChildren<ListBoxItemProps>> = (props) => {
    const { handleClick, id, optionValue, children, ...passThroughProps } = props;
    const { value } = React.useContext(ListBoxContext);
    const active = optionValue === value;

    return (
        <li
            id={id}
            className={cx(listOptionStyle, active && activeStyle)}
            aria-selected={active}
            role="option"
            onClick={() => handleClick && handleClick(optionValue)}
            {...passThroughProps}
        >
            {children}
        </li>
    );
};

interface ListBoxComposition {
    Option: typeof ListBoxItem;
}

type ListBoxComponent = React.ForwardRefExoticComponent<
    React.PropsWithChildren<ListBoxProps> & React.RefAttributes<HTMLUListElement>
> &
    Partial<ListBoxComposition>;

export const ListBox: ListBoxComponent = React.forwardRef(function ListBoxWithForwardRef(
    props: React.PropsWithChildren<ListBoxProps>,
    ref,
) {
    const {
        id,
        value,
        children,
        open,
        maxSize = 7,
        optionHeight = 48,
        'aria-labelledby': ariaLabelledby,
        ...passthroughProps
    } = props;

    const contextValue = React.useMemo(
        () => ({
            value,
        }),
        [value],
    );

    const numberOfChildren = React.Children.count(children);

    const maxHeight = Math.min(maxSize, numberOfChildren) * optionHeight + 16 + 'px';

    return (
        <ul
            ref={ref}
            id={id}
            className={cx(listBoxStyle, numberOfChildren > maxSize && addScrollStyle, open && openStyle)}
            style={{ height: maxSize ? maxHeight : 'auto' }}
            tabIndex={0}
            role="listbox"
            aria-labelledby={ariaLabelledby}
            {...passthroughProps}
        >
            <ListBoxContext.Provider value={contextValue}>{children}</ListBoxContext.Provider>
        </ul>
    );
});

ListBox.Option = ListBoxItem;
